import { Footer as AppFooter } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "@/lib/router-compat";

const PrivacyWebsite = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col">
      <div className="container mx-auto max-w-4xl px-4 py-8 flex-1">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-foreground hover:text-foreground/80"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Terug
        </Button>

        <div className="bg-card rounded-lg p-6 md:p-8 text-foreground">
          <h1 className="text-3xl font-bold mb-6">Privacybeleid Website</h1>
          <p className="text-sm text-muted-foreground mb-8">Laatst bijgewerkt: 27 februari 2026</p>

          <div className="space-y-6 prose prose-sm max-w-none dark:prose-invert">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. Inleiding</h2>
              <p>
                Dit privacybeleid beschrijft hoe Ayemate Online Experts ("wij", "ons", "onze") omgaat met gegevens 
                die worden verzameld bij het bezoeken van de website dehusselapp.nl. Dit beleid geldt voor alle 
                bezoekers, ongeacht of zij een account hebben.
              </p>
              <p className="mt-2">
                Heeft u een account bij De Hussel App? Dan geldt aanvullend ons{" "}
                <a href="/privacy" className="text-primary hover:underline">privacybeleid voor app-gebruikers</a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. Verwerkingsverantwoordelijke</h2>
              <p>
                Ayemate Online Experts<br />
                Chabotstraat 55<br />
                4812XP Breda<br />
                Nederland<br />
                E-mail: <a href="mailto:privacy@ayemate.nl" className="text-primary hover:underline">privacy@ayemate.nl</a>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Cookies</h2>
              <p>Wij gebruiken drie categorieën cookies op onze website:</p>

              <h3 className="text-lg font-medium mb-2 mt-4">3.1 Functionele cookies (noodzakelijk)</h3>
              <p>
                Deze cookies zijn essentieel voor het functioneren van de website. Zonder deze cookies 
                kan de website niet correct werken. Voorbeelden:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Sessie-cookies voor het onthouden van inlogstatus</li>
                <li>Taalvoorkeur</li>
                <li>Themavoorkeur (licht/donker)</li>
                <li>Cookie-voorkeurinstellingen</li>
              </ul>
              <p className="mt-2 text-sm text-muted-foreground">
                Rechtsgrond: gerechtvaardigd belang (noodzakelijk voor de werking van de website).
              </p>

              <h3 className="text-lg font-medium mb-2 mt-4">3.2 Analytische cookies</h3>
              <p>
                Wij gebruiken Google Analytics 4 (GA4) en Google Tag Manager om het websitegebruik 
                te analyseren en de website te verbeteren. Deze cookies verzamelen geanonimiseerde 
                informatie over:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Bezochte pagina's en gebruikersstroom</li>
                <li>Duur van bezoek</li>
                <li>Herkomst van bezoek (referrer)</li>
                <li>Apparaat- en browsertype</li>
                <li>Geografische regio (niet exact adres)</li>
              </ul>
              <p className="mt-2 text-sm text-muted-foreground">
                Rechtsgrond: toestemming. U kunt analytische cookies weigeren via de cookie-instellingen.
              </p>

              <h3 className="text-lg font-medium mb-2 mt-4">3.3 Marketing cookies</h3>
              <p>
                Marketing cookies worden gebruikt om advertenties relevanter te maken voor bezoekers. 
                Deze cookies kunnen worden geplaatst door ons of door derden (zoals Google Ads) en 
                worden gebruikt voor:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Het tonen van relevante advertenties</li>
                <li>Het meten van advertentie-effectiviteit</li>
                <li>Het beperken van het aantal keer dat een advertentie wordt getoond</li>
              </ul>
              <p className="mt-2 text-sm text-muted-foreground">
                Rechtsgrond: toestemming. U kunt marketing cookies weigeren via de cookie-instellingen.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Google Consent Mode v2</h2>
              <p>
                Wij maken gebruik van Google Consent Mode v2 om uw cookie-voorkeuren door te geven 
                aan Google-diensten. Dit zorgt ervoor dat Google Analytics en Google Ads uw voorkeuren 
                respecteren. De volgende consent-signalen worden beheerd:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li><code>analytics_storage</code> — opslag van analytische cookies</li>
                <li><code>ad_storage</code> — opslag van advertentiecookies</li>
                <li><code>ad_user_data</code> — delen van gebruikersgegevens met adverteerders</li>
                <li><code>ad_personalization</code> — personalisatie van advertenties</li>
                <li><code>functionality_storage</code> — opslag voor functionele cookies</li>
                <li><code>security_storage</code> — opslag voor beveiligingscookies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Cookie-voorkeuren wijzigen</h2>
              <p>
                Bij uw eerste bezoek aan onze website wordt u gevraagd om uw cookie-voorkeuren in te stellen. 
                U kunt uw voorkeuren op elk moment wijzigen door de cookie-instellingen te openen via de 
                cookie-banner onderaan de pagina, of door uw browser-cookies te verwijderen.
              </p>
              <p className="mt-2">
                Uw cookie-voorkeuren worden 365 dagen onthouden.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Gegevens die wij verzamelen bij websitebezoek</h2>
              <p>Bij het bezoeken van onze website kunnen de volgende gegevens worden verwerkt:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>IP-adres (geanonimiseerd bij Google Analytics)</li>
                <li>Browser- en apparaattype</li>
                <li>Bezochte pagina's en klikgedrag</li>
                <li>Datum en tijd van bezoek</li>
                <li>Verwijzende website (referrer)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Contactformulier en demoaanvragen</h2>
              <p>
                Als u ons contactformulier invult of een demo aanvraagt, verwerken wij de door u opgegeven 
                gegevens (naam, e-mailadres, bericht) uitsluitend om uw verzoek te behandelen. 
                Deze gegevens worden niet voor andere doeleinden gebruikt.
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                Rechtsgrond: uitvoering van een overeenkomst (precontractuele fase).
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Verwerkers</h2>
              <p>Wij maken gebruik van de volgende dienstverleners:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Google Analytics / Google Tag Manager</strong> — websiteanalyse (VS, EU Standard Contractual Clauses)</li>
                <li><strong>Vercel / Lovable</strong> — hosting van de website (EU/VS)</li>
                <li><strong>Resend</strong> — e-mailverzending (VS, DPA aanwezig)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Bewaartermijnen</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Cookie-voorkeuren:</strong> 365 dagen</li>
                <li><strong>Google Analytics data:</strong> 14 maanden (GA4 standaard)</li>
                <li><strong>Contactformuliergegevens:</strong> maximaal 1 jaar na afhandeling</li>
                <li><strong>Serverlogboeken:</strong> maximaal 30 dagen</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. Uw rechten (AVG)</h2>
              <p>U heeft het recht op:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Inzage</strong> — welke gegevens wij van u verwerken</li>
                <li><strong>Rectificatie</strong> — correctie van onjuiste gegevens</li>
                <li><strong>Verwijdering</strong> — wissing van uw gegevens</li>
                <li><strong>Beperking</strong> — beperking van de verwerking</li>
                <li><strong>Bezwaar</strong> — bezwaar tegen verwerking op basis van gerechtvaardigd belang</li>
                <li><strong>Intrekking toestemming</strong> — u kunt uw cookie-toestemming op elk moment intrekken</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">11. Klachten</h2>
              <p>
                Heeft u een klacht over de verwerking van uw gegevens? Neem dan contact met ons op. 
                U kunt ook een klacht indienen bij de Autoriteit Persoonsgegevens:{" "}
                <a href="https://www.autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  www.autoriteitpersoonsgegevens.nl
                </a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">12. Contact</h2>
              <p>
                Ayemate Online Experts<br />
                Chabotstraat 55, 4812XP Breda<br />
                E-mail: <a href="mailto:privacy@ayemate.nl" className="text-primary hover:underline">privacy@ayemate.nl</a><br />
                Website: <a href="https://ayemate.nl" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">ayemate.nl</a>
              </p>
            </section>
          </div>
        </div>
      </div>
      <AppFooter />
    </div>
  );
};

export default PrivacyWebsite;
